import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

export default function ExpandableCard({ id, index, name, price, badge, tagline, details, steps, owner, ctaLabel, onCta, ctaTestId, group }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!group) return;
    const handler = (e) => {
      if (e.detail.group === group && e.detail.id !== id) setOpen(false);
    };
    window.addEventListener("expandable-open", handler);
    return () => window.removeEventListener("expandable-open", handler);
  }, [group, id]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail === id) setOpen(true);
    };
    window.addEventListener("open-service", handler);
    return () => window.removeEventListener("open-service", handler);
  }, [id]);

  return (
    <div id={id} data-testid={`card-${id}`} className={`h-full scroll-mt-28 border transition-colors duration-300 ${open ? "border-[#E05A2C] bg-[#16110D]" : "border-[#2A241F] hover:border-[#E05A2C] hover:bg-[#16110D]"}`}>
      <button
        data-testid={`toggle-${id}`}
        onClick={() => {
          setOpen((v) => {
            if (!v && group) window.dispatchEvent(new CustomEvent("expandable-open", { detail: { group, id } }));
            return !v;
          });
        }}
        aria-expanded={open}
        className="group flex min-h-[56px] w-full items-center justify-between gap-3 px-4 py-3 text-left sm:gap-6 sm:p-8"
      >
        <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-4 gap-y-2">
          <div className="min-w-0">
            <span className="block font-heading text-base font-black uppercase tracking-tight text-[#FAF7F2] sm:text-2xl">{name}</span>
            <span className="mt-1 hidden text-sm text-[#A89F91] sm:block">{tagline}</span>
          </div>
          {price ? (
            <span data-testid={`price-${id}`} className="font-mono text-base font-semibold text-[#D4A359] sm:text-lg">{price}</span>
          ) : (
            <span data-testid={`badge-${id}`} className="rounded-full border border-[#4D2C20] bg-[#2B1B15] px-4 py-1.5 font-mono text-xs uppercase tracking-widest text-[#E05A2C] sm:text-xs">{badge}</span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3 sm:gap-5">
          <span className="flex items-center gap-3">
            <span className="hidden font-mono text-xs uppercase tracking-widest text-[#A89F91] transition-colors duration-300 group-hover:text-[#E05A2C] sm:inline">
              {open ? "Chiudi" : "Approfondisci"}
            </span>
            <span className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors duration-300 ${open ? "border-[#E05A2C] bg-[#E05A2C] text-white" : "border-[#383029] text-[#A89F91] group-hover:border-[#E05A2C] group-hover:text-[#E05A2C]"}`}>
              {open ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            </span>
          </span>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-[#2A241F] px-6 pb-8 pt-6 sm:px-8">
              <ul className="max-w-2xl space-y-3">
                {details.map((d) => (
                  <li key={d} className="flex gap-4 text-sm leading-relaxed text-[#C4BBB0] sm:text-base">
                    <span className="mt-2 h-px w-5 shrink-0 bg-[#E05A2C]" />
                    {d}
                  </li>
                ))}
              </ul>
              {steps && (
                <div data-testid={`steps-${id}`} className="mt-8 border-t border-[#2A241F] pt-6">
                  <p className="font-mono text-xs uppercase tracking-widest text-[#E05A2C]">{steps.title}</p>
                  <ol className="mt-4 grid gap-3 sm:grid-cols-5 sm:gap-4">
                    {steps.items.map((s, i) => (
                      <li key={s.name} className="flex gap-3 sm:block">
                        <span className="font-heading text-lg font-black leading-none text-[#E05A2C] sm:text-2xl">{i + 1}</span>
                        <span className="sm:mt-2 sm:block">
                          <span className="block text-sm font-medium text-[#FAF7F2]">{s.name}</span>
                          <span className="mt-0.5 block text-sm leading-snug text-[#A89F91]">{s.text}</span>
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {owner && (
                <p data-testid={`owner-${id}`} className="mt-6 font-mono text-xs uppercase tracking-widest text-[#A89F91]">
                  Chi se ne occupa — <span className="text-[#E05A2C]">{owner}</span>
                </p>
              )}
              <button
                data-testid={ctaTestId}
                onClick={onCta}
                className="btn-glow mt-8 rounded-full border border-[#383029] px-8 py-3.5 text-sm font-medium text-[#FAF7F2] transition-colors duration-300 hover:border-[#E05A2C] active:scale-[0.98]"
              >
                {ctaLabel}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
